import { useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { AlertTriangle, Send, ShieldCheck, Clock } from "lucide-react";

const CLASS_LABEL = {
  PRAMAANA_POSITION: { label: "PRAMAANA POSITION", cls: "status-CANONICAL" },
  FACT: { label: "FACT", cls: "status-CANONICAL" },
  FOUNDER_VIEW: { label: "FOUNDER VIEW", cls: "status-HYPOTHESIS" },
  HYPOTHESIS: { label: "HYPOTHESIS", cls: "status-HYPOTHESIS" },
  OPEN: { label: "OPEN — UNRESOLVED", cls: "status-OPEN" },
  HISTORICAL: { label: "HISTORICAL", cls: "status-SUPERSEDED" },
};

export default function AskPramaana() {
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState("CURRENT");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const ask = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/ask", { question, mode });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Answer generation failed");
    } finally {
      setLoading(false);
    }
  };

  const cls = result && (CLASS_LABEL[result.classification] || { label: result.classification, cls: "status-WORKING" });

  return (
    <div>
      <PageHeader
        title="Ask Pramaana"
        subtitle="Answers are grounded strictly in founder-approved knowledge and governed by the authority hierarchy. Provenance is always shown."
      />
      <div className="p-10">
        <div className="bg-white border border-slate-200 p-6 mb-8">
          <div className="flex gap-2 mb-4" data-testid="ask-mode-toggle">
            {[
              { k: "CURRENT", label: "What does PRAMAANA believe?" },
              { k: "HISTORICAL", label: "How did PRAMAANA arrive here?" },
            ].map((m) => (
              <button
                key={m.k}
                data-testid={`mode-${m.k.toLowerCase()}`}
                onClick={() => setMode(m.k)}
                className={`px-4 py-2 text-sm border rounded-sm transition-colors duration-150 ${
                  mode === m.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <Textarea
            data-testid="ask-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Does PRAMAANA verify that a priest received the Dakshina?"
            className="rounded-sm border-slate-300 min-h-[90px] text-base"
          />
          <div className="flex justify-end mt-4">
            <Button data-testid="ask-submit" onClick={ask} disabled={loading} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
              <Send size={15} /> {loading ? "Consulting the corpus…" : "Ask Pramaana"}
            </Button>
          </div>
        </div>

        {error && <div data-testid="ask-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-sm">{error}</div>}

        {result && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 fade-in" data-testid="ask-result">
            <div className="lg:col-span-8">
              <div className="bg-white border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <span data-testid="answer-classification" className={`inline-flex items-center border px-2.5 py-1 rounded-sm font-mono text-[11px] uppercase tracking-wider ${cls.cls}`}>
                    {cls.label}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500 font-mono">
                    {result.is_current_doctrine ? <ShieldCheck size={14} className="text-emerald-600" /> : <Clock size={14} />}
                    {result.is_current_doctrine ? "CURRENT DOCTRINE" : "HISTORICAL CONTEXT"}
                  </span>
                  {typeof result.certainty === "number" && (
                    <span className="text-xs text-slate-400 font-mono ml-auto">certainty {Math.round(result.certainty * 100)}%</span>
                  )}
                </div>

                {result.unresolved && (
                  <div data-testid="unresolved-banner" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm mb-5">
                    <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                    <span className="text-sm">This matter is governed by an <strong>OPEN question</strong> and remains formally unresolved. PRAMAANA does not assert a settled position here.</span>
                  </div>
                )}

                <div className="font-serif text-xl leading-relaxed text-slate-800 whitespace-pre-wrap" data-testid="answer-text">
                  {result.answer}
                </div>

                {result.notes && (
                  <p className="text-sm text-slate-500 mt-6 border-t border-slate-100 pt-4">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Caveat</span><br />{result.notes}
                  </p>
                )}
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-white border border-slate-200 p-6 sticky top-6" data-testid="provenance-panel">
                <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-4">Provenance</h3>

                <ProvSection label="Knowledge Used" ids={result.knowledge_ids_used} />
                <ProvSection label="Decisions Used" ids={result.decision_ids_used} />
                <ProvSection label="Open Questions Triggered" ids={result.open_questions_triggered} danger />

                {result.provenance?.length > 0 && (
                  <div className="mt-5 border-t border-slate-100 pt-4 space-y-3">
                    {result.provenance.map((p) => (
                      <div key={p.knowledge_id} className="border-l-2 border-slate-200 pl-3" data-testid={`prov-${p.knowledge_id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-700 bg-slate-100 px-1.5 rounded-sm">{p.knowledge_id}</span>
                          <StatusBadge status={p.status} />
                        </div>
                        <div className="text-xs text-slate-600 leading-relaxed">{p.statement}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProvSection({ label, ids, danger }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {ids.map((id) => (
          <span key={id} className={`font-mono text-xs px-1.5 py-0.5 rounded-sm ${danger ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-slate-100 text-slate-700"}`}>
            {id}
          </span>
        ))}
      </div>
    </div>
  );
}
